import { Injectable } from '@angular/core';
import { Dataset } from '../../core/store/models/dataset.model';
import { Layer } from '../store/cop/models/layer.model';
import { GeoJsonUtilityService } from './geo-json-utility.service';
import { SegmentProperties } from './models/traveler.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { Feature, FeatureCollection } from 'geojson';
import { cloneDeep } from 'lodash-es';

const DEFAULT_SEGMENT_FIELD = 'segments';
const ONE_HOUR: number = 60 * 60 * 1000;
const TWO_HOURS: number = 2 * 60 * 60 * 1000;
const THIRTY_MINUTES: number = 30 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class TravelerService {
  constructor() { }

  public static calculateTravelerFeatureVisibility(
    travelerSegmentProperties: SegmentProperties[],
    currentSegment: SegmentProperties,
    mobileLocationGeo?: Feature,
    mobileLocationDate?: number
  ): [boolean, number, boolean] {
    let visibilityToggle = true;
    let nextVisible = Number.MAX_SAFE_INTEGER;

    const now = new Date().getTime();

    switch (currentSegment.segmentType) {
      case 'air':
        // For Air segments, pin placement should be at the "from" airport 2 hours prior to the departure and 30 minutes after departure.
        visibilityToggle = currentSegment.fromTime - TWO_HOURS <= now && currentSegment.fromTime + THIRTY_MINUTES >= now;
        if (!visibilityToggle && currentSegment.fromTime - TWO_HOURS > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime - TWO_HOURS);
        }
        if (currentSegment.isOriginSegment) {
          break;
        }
        // After that, pin placement should be at the "to" airport until 2 hours after arrival.
        visibilityToggle = !visibilityToggle && currentSegment.toTime <= now && currentSegment.toTime + TWO_HOURS >= now;
        if (!visibilityToggle && currentSegment.toTime > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.toTime);
        }
        break;
      case 'rail':
        // For Rail segments, pin placement should be at the "from" station 1 hours prior to the departure and 30 minutes after departure.
        visibilityToggle = currentSegment.fromTime - ONE_HOUR <= now && currentSegment.fromTime + THIRTY_MINUTES >= now;
        if (!visibilityToggle && currentSegment.fromTime - ONE_HOUR > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime - ONE_HOUR);
        }
        if (currentSegment.isOriginSegment) {
          break;
        }
        // After that, pin placement should be at the "to" station until 1 hours after arrival.
        visibilityToggle = !visibilityToggle && currentSegment.fromTime <= now && currentSegment.toTime + ONE_HOUR >= now;
        if (!visibilityToggle && currentSegment.fromTime + THIRTY_MINUTES > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime + THIRTY_MINUTES);
        }
        break;
      case 'dining':
        // For Dining segments, pin placement should be at the restaurant from the start time until the start time plus 2 hours.
        visibilityToggle = currentSegment.fromTime <= now && currentSegment.fromTime + TWO_HOURS >= now;
        if (!visibilityToggle && currentSegment.fromTime > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime);
        }
        break;
      case 'hotel':
        visibilityToggle = currentSegment.fromTime <= now && currentSegment.toTime >= now;
        if (!visibilityToggle && currentSegment.fromTime > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime);
        }
        break;
      case 'car':
        // TODO: These rules recursively conflict with each other, Mike needs to resolve them - For now, render them all except car if there are dining conflicts
        // For Hotel segments, pin placement should be at the hotel during the stay, unless a Car or Dining segment overlaps it,
        // in that case pin placement will follow the Car or Dining segment rules.

        // For Car segments, pin placement should be at the car rental address from the start time until the end time, unless a Dining
        // or Hotel segment overlaps it.
        visibilityToggle = currentSegment.fromTime <= now && currentSegment.toTime >= now;
        if (!visibilityToggle && currentSegment.fromTime > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime);
        }

        const overlappingSegments = travelerSegmentProperties.filter(
          t =>
            t.segmentType === 'dining' &&
            (!currentSegment.segmentId || t.segmentId !== currentSegment.segmentId) &&
            t.fromTime >= currentSegment.fromTime &&
            t.toTime <= currentSegment.toTime
        );

        // if overlap don't show myself
        if (visibilityToggle && overlappingSegments.length > 0) {
          // if any of the overlap segments are shown then false
          overlappingSegments.forEach(seg => {
            const [visibleNow, visibleWhen] = this.calculateTravelerFeatureVisibility(travelerSegmentProperties, seg);
            if (visibleNow) {
              visibilityToggle = false;
            }
            if (!visibleNow && visibleWhen) {
              // If this other item is not visible, but may be in the future, report it as a posible state change time
              nextVisible = Math.min(nextVisible, visibleWhen);
            }
          });
        }
        break;
      default:
        // Display any unknown segment types if their time is valid
        visibilityToggle = currentSegment.fromTime <= now && currentSegment.toTime >= now;
        if (!visibilityToggle && currentSegment.fromTime > now) {
          // If visible time is in the future, then add it as a possible trigger time
          nextVisible = Math.min(nextVisible, currentSegment.fromTime);
        }
        break;
    }

    var showMobileGeo = false;
    if (!!mobileLocationGeo && !!mobileLocationDate) {
      showMobileGeo = currentSegment.fromTime < mobileLocationDate && mobileLocationDate < currentSegment.toTime;
    }

    return [visibilityToggle, visibilityToggle || nextVisible === Number.MAX_SAFE_INTEGER ? 0 : nextVisible, showMobileGeo];
  }

  public static geoPropertiesToSegmentPropertiesObject(currentProperties: {
    from_date?: string;
    to_date?: string;
    is_origin_segment?: boolean;
    segment_type?: string;
    segment_id?: string;
  }) {
    if (!currentProperties) {
      return {} as SegmentProperties; // Callers will have to handle no values in the array
    }

    var segmentProp = {
      fromTime: new Date(currentProperties?.from_date).getTime(),
      toTime: new Date(currentProperties?.to_date).getTime(),
      isOriginSegment: !!currentProperties?.is_origin_segment,
      segmentId: currentProperties?.segment_id,
      segmentType: !currentProperties?.segment_type ? '' : currentProperties.segment_type.toLocaleLowerCase()
    } as SegmentProperties;

    return segmentProp;
  }

  static getSegmentField(dataset: Dataset, layer: Layer): string {
    let geoField = dataset.schema.fields.find(f => f.systemName === DEFAULT_SEGMENT_FIELD);
    if (!!!geoField) {
      geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);
    }

    return geoField?.systemName;
  }

  static getSegmentsForDisplay(document: Document): any[] {
    if (!document?.segments) {
      return [];
    }
    const segments = JSON.parse(document.segments) as Document[];

    var geometryFeatures = segments
      ?.map(s => (s['geometry'] ? GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(JSON.parse(s['geometry']))[0] : null))
      .filter(g => g);

    return geometryFeatures;
  }

  static getMobileLocationShareGeo(document: Document): any {
    if (!document?.geometry) {
      return [];
    }
    const featureCollection = JSON.parse(document.geometry) as FeatureCollection;

    if (!featureCollection?.features) {
      return [];
    }

    var feature = featureCollection.features.find(
      f => f.properties != null && !!f.properties['LocationType'] && f.properties['LocationType'] === 'Location Share'
    );
    if (!feature?.properties || !feature?.properties['MobileLocationShareDate']) {
      return [];
    }

    return [feature, feature.properties['MobileLocationShareDate']];
  }

  // corresponds to svg names in backend image service
  static getItineraryIcon(type: string): string {
    switch (type?.toLocaleLowerCase()) {
      default:
        return null;
      case 'air':
        return 'small-solid-airplane-2';
      case 'car':
        return 'small-solid-car-2';
      case 'ride':
        return 'small-solid-car-2';
      case 'dining':
        return 'small-solid-coffee';
      case 'hotel':
        return 'small-solid-hotel';
      case 'rail':
        return 'small-solid-train';
      case 'mobilelocationshare':
        return 'small_solid_mobile_phone_check';
      case 'safety':
        return 'small_solid_mobile_phone_check';
      case 'mobile':
        return 'small_solid_mobile_phone_check';
      case 'checkin':
        return 'small_solid_mobile_phone_check';
      case 'crisissignal':
        return 'mobile-phone-sos';
    }
  }

  static getItineraryColor(properties: SegmentProperties): string {
    if (properties.segmentType?.toLocaleLowerCase() === 'crisissignal') {
      return 'EE0000';
    }
    else {
      return new Date(properties.fromTime) < new Date() ? '2F9E44' : '70C3FF';
    }
  }
}

